import {
  Component,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule, AgGridAngular } from 'ag-grid-angular';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
  CellContextMenuEvent,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
// models
import { FFLLookupColumnDef } from './columnDefs';
import { SharedService } from '../shared/services/shared.service';
import { AgGridService } from '../shared/services/ag-grid.service';
import { LocalDatabaseService } from '../shared/services/local-database.service';
import { AtfFflSyncService } from '../shared/services/atf-ffl-sync.service';
import { isObjectEmpty } from '../shared/functions';
import {
  ComponentNames,
  ModalID,
  StatusModalIndicator,
} from '../shared/models/constants';
import { ContextMenuService } from '../shared/services/context-menu.service';

@Component({
  selector: 'app-ffl-lookup',
  standalone: true,
  imports: [CommonModule, AgGridModule],
  templateUrl: './ffl-lookup.component.html',
  styleUrl: './ffl-lookup.component.scss',
})
export class FflLookupComponent {
  rowData = [];
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular;
  menuState$ = this.contextMenuService.menuState$;

  columnDefs: ColDef[] = FFLLookupColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;
  lastSyncDate: string | null = null;

  modalRef: BsModalRef;
  modalConfig = {
    animated: true,
  };

  constructor(
    public sharedService: SharedService,
    private modalService: BsModalService,
    private agGridService: AgGridService,
    private localDb: LocalDatabaseService,
    private atfFflSyncService: AtfFflSyncService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
  ) {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this };
    this.gridOptions.rowClass = 'ag-grid-row';
    // Default (client-side) row model — always reads from the atfFflList
    // IndexedDB cache, which AtfFflSyncService keeps in step with Cosmos in
    // the background. The grid never talks to the server directly.
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
  }

  ngOnInit(): void {
    this.rowData = [];
  }

  async onGridReady(params: any) {
    this.gridApi = params.api;

    const cachedCount = await this.localDb.atfFflList.count();
    if (cachedCount === 0) {
      // Nothing local yet (new device, or the login-time background sync
      // hasn't landed) — sync now so the grid isn't just empty.
      this.sharedService.openStatusSpinner('Loading FFL List');
      await this.atfFflSyncService.sync().catch(() => {});
      this.sharedService.closeStatusSpinner();
    }
    await this.loadFromCache();

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.ffl_management)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.ffl_management,
      );
    }
    this.applyCurrentSessionPreferences();
  }

  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }

  openHome() {
    this.sharedService.changeRoute('home');
  }

  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Syncing FFL List');
    await this.atfFflSyncService.sync().catch(() => {
      this.sharedService.raiseErrorToast('Failed to sync FFL list — showing last cached copy.');
    });
    await this.loadFromCache();
    this.sharedService.closeStatusSpinner();
  }

  private async loadFromCache(): Promise<void> {
    this.rowData = await this.localDb.atfFflList.toArray();
    this.lastSyncDate = await this.atfFflSyncService.getLastSyncedAt();
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.ffl_management,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.ffl_management,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.ffl_management,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.ffl_management,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.ffl_management,
      filters,
      column_state,
    );
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}
